// Rack filter sentinel; ALL_RACKS shows every node regardless of rack.
export const ALL_RACKS = "__all__";

// A rack name is only unique within a site, so options are keyed by site+rack.
// Keying by rack name alone would collapse identically named racks from
// different sites into one option (and, in the staff allocation picker, would
// let "Select all shown" stage nodes across sites). The NUL separator can't
// appear in a site or rack name.
export type RackOption = { key: string; site: string | null; rack: string | null; count: number };

export function rackKeyOf(node: { site?: string | null; rack?: string | null }): string {
  return `${node.site ?? ""}\u0000${node.rack ?? ""}`;
}

export function rackOptionLabel(option: RackOption): string {
  const rackPart = option.rack ? `Rack ${option.rack}` : "Unracked";
  return option.site ? `${rackPart} · ${option.site}` : rackPart;
}

// Distinct site+rack groups present in the given nodes, each with a count,
// naturally sorted by site then rack (so "Rack 2" precedes "Rack 10"), with
// unracked nodes last.
export function rackOptionsFrom(
  nodes: readonly { site?: string | null; rack?: string | null }[],
): RackOption[] {
  const map = new Map<string, RackOption>();
  for (const n of nodes) {
    const key = rackKeyOf(n);
    const existing = map.get(key);
    if (existing) existing.count += 1;
    else map.set(key, { key, site: n.site ?? null, rack: n.rack ?? null, count: 1 });
  }
  return [...map.values()].sort((a, b) => {
    if (a.rack === null && b.rack !== null) return 1;
    if (b.rack === null && a.rack !== null) return -1;
    const siteCmp = (a.site ?? "").localeCompare(b.site ?? "", undefined, { numeric: true });
    if (siteCmp !== 0) return siteCmp;
    return (a.rack ?? "").localeCompare(b.rack ?? "", undefined, { numeric: true });
  });
}
